
import { STATIC_BADGES, EVOLVING_BADGES } from '../badges';

// 教科名のマッピング
const SUBJECT_MAP = {
  '国語': 'japanese',
  '現代文': 'japanese',
  '古文': 'japanese',
  '漢文': 'japanese',
  '数学': 'math',
  '数学I': 'math',
  '数学A': 'math',
  '数学II': 'math',
  '数学B': 'math',
  '数学III': 'math',
  '数学C': 'math',
  '英語': 'english',
  '英単語': 'english',
  '英文法': 'english',
  '理科': 'science',
  '物理': 'science',
  '化学': 'science',
  '生物': 'science',
  '地学': 'science',
  '社会': 'social',
  '地理': 'social',
  '歴史': 'social',
  '日本史': 'social',
  '世界史': 'social',
  '公民': 'social',
  '倫理': 'social',
  '政治経済': 'social',
};

/**
 * 現在の値からバッジのレベル情報を計算する
 */
export const calculateBadgeLevel = (badgeId, currentValue) => {
  const badgeDef = EVOLVING_BADGES[badgeId];
  if (!badgeDef) return null;

  const thresholds = badgeDef.thresholds;
  let currentLevelIdx = 0;

  // 現在の値がどのレベル帯にいるか探す
  for (let i = 0; i < thresholds.length; i++) {
    if (currentValue >= thresholds[i].min) {
      currentLevelIdx = i;
    } else {
      break;
    }
  }

  const currentLevelData = thresholds[currentLevelIdx];
  const nextLevelData = thresholds[currentLevelIdx + 1];

  let progress = 0;
  let nextThreshold = null;

  if (nextLevelData) {
    // 次のレベルがある場合、進捗率を計算
    const range = nextLevelData.min - currentLevelData.min;
    const valueInLevel = currentValue - currentLevelData.min;
    progress = Math.min(100, Math.floor((valueInLevel / range) * 100));
    nextThreshold = nextLevelData.min;
  } else {
    // 最高レベル到達
    progress = 100;
  }

  return {
    ...badgeDef,
    level: currentLevelData.level,
    label: currentLevelData.label,
    color: currentLevelData.color, // bronze, silver, gold, etc.
    currentValue,
    nextThreshold,
    progress
  };
};

/**
 * 新しい記録保存時に呼び出し、獲得/レベルアップしたバッジを返す
 */
export const checkNewBadges = (currentUser, recordData, addedMinutes) => {
  const newBadges = [];
  const acquiredStatic = new Set(currentUser.acquiredBadges || []);
  const currentStats = currentUser.badgeStats || {}; // { math: 100, total_time: 500 ... }

  // --- 1. 固定バッジの判定 ---
  const unlockStatic = (id) => {
    if (!acquiredStatic.has(id) && STATIC_BADGES[id]) {
      newBadges.push({ type: 'static', badge: STATIC_BADGES[id] });
      acquiredStatic.add(id);
    }
  };

  unlockStatic('first_record');
  if (addedMinutes >= 30) unlockStatic('study_30_min');
  if (addedMinutes >= 120) unlockStatic('long_session');
  if (addedMinutes >= 180) unlockStatic('endurance_runner'); // 3時間
  if (addedMinutes >= 300) unlockStatic('iron_will'); // 5時間

  const recordDate = recordData.timestamp.toDate ? recordData.timestamp.toDate() : new Date(recordData.timestamp);
  const hour = recordDate.getHours();
  const day = recordDate.getDay(); // 0:Sun, 1:Mon, ..., 5:Fri, 6:Sat

  if (hour >= 5 && hour < 9) unlockStatic('morning_glory');
  if (hour >= 12 && hour < 13) unlockStatic('lunch_learner'); // ランチタイム
  if (hour >= 22 || hour < 4) unlockStatic('night_owl');
  
  if (day === 5 && hour >= 20) unlockStatic('friday_night_fever'); // 金曜20時以降
  if (day === 0 || day === 6) unlockStatic('weekend_warrior');


  // --- 2. 進化型バッジの判定 ---
  // 更新前の値を取得
  const subjectKey = SUBJECT_MAP[recordData.title] || 'other';
  
  // チェック対象の進化バッジキーと、追加される値
  const checkTargets = [
    { key: 'total_time', add: addedMinutes },
    { key: subjectKey, add: addedMinutes },
    // streakは別途計算が必要だが、今回は簡易的に「今日記録したら+1」と仮定して判定（厳密には呼び出し元でstreak計算済み推奨）
    // ここではRecordForm側でstreak更新後の値を渡してもらうか、簡易チェックに留める
  ];

  checkTargets.forEach(({ key, add }) => {
    if (!EVOLVING_BADGES[key]) return;

    const oldValue = currentStats[key] || 0;
    const newValue = oldValue + add;

    const oldLevelInfo = calculateBadgeLevel(key, oldValue);
    const newLevelInfo = calculateBadgeLevel(key, newValue);

    if (newLevelInfo.level > oldLevelInfo.level) {
      newBadges.push({
        type: 'evolve',
        badgeId: key,
        badgeName: EVOLVING_BADGES[key].name,
        oldLevel: oldLevelInfo.level,
        newLevel: newLevelInfo.level,
        info: newLevelInfo
      });
    }
  });

  return newBadges;
};

// ユーティリティ: 教科名からキーを取得
export const getSubjectKey = (title) => SUBJECT_MAP[title] || 'other';
